import React, { useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, UploadCloud, Boxes, Image as ImageIcon, Music, FileCode2, Database, Layers,
  Archive, Ban, File as FileIcon, Loader2, ShieldCheck, Wand2, CheckCircle2, AlertTriangle,
  CircleSlash, Sparkles, Rocket,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const CAT_ICON = {
  "3d": Boxes, image: ImageIcon, audio: Music, code: FileCode2, data: Database,
  project: Layers, archive: Archive, binary: Ban, other: FileIcon,
};
const COMPAT = {
  direct: { label: "Direct", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 },
  convert: { label: "Auto-convert", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40", Icon: Wand2 },
  ai: { label: "AI rebuild", cls: "bg-violet-500/15 text-violet-300 border-violet-500/40", Icon: Sparkles },
  unsupported: { label: "Unsupported", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", Icon: CircleSlash },
};
const REPORT_STATS = [
  { key: "direct", label: "Directly compatible", color: "#34d399" },
  { key: "convert", label: "Auto-converted", color: "#38bdf8" },
  { key: "ai", label: "AI reconstruction", color: "#a78bfa" },
  { key: "unsupported", label: "Unsupported", color: "#fb7185" },
];

export default function UniversalImport() {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [rights, setRights] = useState(false);
  const [job, setJob] = useState(null);           // {job_id, files, report, ...}
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [converting, setConverting] = useState(false);
  const [report, setReport] = useState(null);
  const fileRef = useRef(null);

  const files = job?.files || [];

  const startJob = useCallback(async () => {
    if (!rights) { toast.error("Please confirm you own or are licensed to use this content."); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/import/jobs", { name: name || "Imported project", rights_confirmed: true });
      setJob(data);
      toast.success("Import started — add your project files.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start the import."); }
    finally { setCreating(false); }
  }, [name, rights]);

  const onPick = useCallback(async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length || !job) return;
    setUploading(true);
    try {
      const fd = new FormData();
      picked.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/import/jobs/${job.job_id}/files`, fd, { timeout: 180000 });
      setJob((j) => ({ ...j, files: data.files, report: data.report }));
      setReport(null);
      toast.success(`Added ${data.added} file${data.added === 1 ? "" : "s"}.`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed."); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }, [job]);

  const analyze = useCallback(async () => {
    if (!job) return;
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/import/jobs/${job.job_id}/analyze`, {}, { timeout: 120000 });
      setReport(data);
      toast.success("Compatibility report ready.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed."); }
    finally { setAnalyzing(false); }
  }, [job]);

  const convert = useCallback(async () => {
    if (!job) return;
    setConverting(true);
    try {
      const { data } = await api.post(`/import/jobs/${job.job_id}/convert`, {}, { timeout: 180000 });
      toast.success("Converted! Opening your new game…");
      setTimeout(() => navigate(`/app/build/${data.game_id}`), 900);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Conversion failed."); }
    finally { setConverting(false); }
  }, [job, navigate]);

  const rep = report || job?.report;

  return (
    <div className="min-h-screen bg-[#070912] text-white" data-testid="universal-import">
      <div className="max-w-5xl mx-auto p-5 lg:p-8">
        <div className="flex items-center gap-3 mb-2">
          <Button variant="ghost" size="icon" onClick={() => navigate("/app/create")} data-testid="import-back" className="text-white/70 hover:text-white"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex items-center gap-2">
            <UploadCloud className="w-6 h-6 text-[#3b9eff]" />
            <h1 className="font-display text-2xl lg:text-3xl font-extrabold tracking-tight">Universal Import</h1>
          </div>
        </div>
        <p className="text-sm text-white/50 mb-6 pl-12 max-w-2xl">
          Bring in your own assets or a game project. GameView classifies everything, shows an honest
          compatibility report, then translates the compatible pieces into a real GameView game via the Game IR.
          We don't promise perfect conversion.
        </p>

        {/* Step 1 — rights + start */}
        {!job && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 lg:p-6 max-w-2xl" data-testid="import-start">
            <label className="text-xs font-bold text-white/60 uppercase tracking-wide">Import name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. My Unity prototype"
              data-testid="import-name" className="mt-1.5 bg-white/5 border-white/15 text-white" />
            <label className="flex items-start gap-3 mt-5 cursor-pointer select-none" data-testid="import-rights-label">
              <input type="checkbox" checked={rights} onChange={(e) => setRights(e.target.checked)}
                data-testid="import-rights-checkbox" className="mt-0.5 w-4 h-4 accent-[#3b9eff]" />
              <span className="text-sm text-white/75">
                <ShieldCheck className="w-4 h-4 inline mr-1 -mt-0.5 text-emerald-400" />
                I own, created, or am licensed to use all the content I'm importing.
              </span>
            </label>
            <Button onClick={startJob} disabled={!rights || creating} data-testid="import-start-btn"
              className="mt-5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#3b9eff] hover:opacity-90 text-white">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Start import
            </Button>
          </div>
        )}

        {job && (
          <>
            {/* Step 2 — upload */}
            <input ref={fileRef} type="file" multiple hidden onChange={onPick} data-testid="import-file-input" />
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="import-add-files-btn"
                className="rounded-full bg-white/10 hover:bg-white/15 text-white border border-white/15">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Add files
              </Button>
              <span className="text-sm text-white/50">{files.length} file{files.length === 1 ? "" : "s"} added</span>
              {files.length > 0 && (
                <>
                  <Button onClick={analyze} disabled={analyzing} data-testid="import-analyze-btn"
                    variant="outline" className="rounded-full border-white/20 bg-transparent text-white hover:bg-white/10 ml-auto">
                    {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />} Analyze
                  </Button>
                  <Button onClick={convert} disabled={converting} data-testid="import-convert-btn"
                    className="rounded-full bg-gradient-to-r from-emerald-500 to-[#3b9eff] hover:opacity-90 text-white">
                    {converting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Convert Project
                  </Button>
                </>
              )}
            </div>

            {/* Empty dropzone hint */}
            {files.length === 0 && (
              <button onClick={() => fileRef.current?.click()} data-testid="import-dropzone"
                className="w-full rounded-2xl border-2 border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] p-12 text-center transition-colors">
                <UploadCloud className="w-10 h-10 mx-auto text-white/40 mb-3" />
                <p className="text-white/70 font-semibold">Drop in GLB, PNG, MP3, JSON, source files or a project export</p>
                <p className="text-white/40 text-sm mt-1">GLB/glTF · PNG/JPG/WebP/SVG · WAV/MP3/OGG · JS/TS/C#/Lua/GDScript · Unity/Godot/Three.js/Phaser</p>
              </button>
            )}

            {/* Compatibility Report */}
            {rep && files.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 mb-5" data-testid="import-report">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  <h3 className="font-display font-bold">Import Compatibility Report</h3>
                  <span className="rounded-full bg-white/5 border border-white/10 px-2.5 py-0.5 text-xs font-mono text-white/60" data-testid="import-project-type">{rep.project_label}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {REPORT_STATS.map((s) => (
                    <div key={s.key} className="rounded-xl border border-white/10 bg-black/30 p-3" data-testid={`import-stat-${s.key}`}>
                      <div className="text-2xl font-bold font-mono" style={{ color: s.color }}>{rep[s.key] ?? 0}</div>
                      <div className="text-[11px] text-white/55 mt-0.5">{s.label}</div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-white/45" data-testid="import-report-summary">
                  {rep.found} asset{rep.found === 1 ? "" : "s"} found · {rep.convertible} will be brought into your GameView game.
                </p>
                {rep.ir_preview && (
                  <div className="mt-3 rounded-xl border border-[#7c3aed]/40 bg-[#7c3aed]/10 p-3" data-testid="import-ir-preview">
                    <p className="text-xs text-[#c4b5fd] font-semibold flex items-center gap-1.5">
                      <FileCode2 className="w-3.5 h-3.5" /> AI parsed your source into Game IR
                    </p>
                    <p className="text-[11px] text-white/60 mt-1">
                      Detected a <span className="font-mono text-white/80">{rep.ir_preview.template}</span> ({rep.ir_preview.dimension?.toUpperCase()}) — {rep.ir_preview.total} IR nodes across {Object.keys(rep.ir_preview.counts || {}).length} subsystems.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* File inventory */}
            {files.length > 0 && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="import-file-list">
                <h3 className="font-bold text-sm mb-3">Assets ({files.length})</h3>
                <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
                  {files.map((f, i) => {
                    const Icon = CAT_ICON[f.category] || FileIcon;
                    const cm = COMPAT[f.compat] || COMPAT.unsupported;
                    return (
                      <div key={i} data-testid={`import-file-${i}`} className="flex items-center gap-3 rounded-xl bg-black/25 border border-white/8 px-3 py-2">
                        <Icon className="w-4 h-4 text-white/50 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{f.name}</div>
                          <div className="text-[11px] text-white/40 truncate">{f.format}{f.note ? ` · ${f.note}` : ""}</div>
                        </div>
                        <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold ${cm.cls}`} data-testid={`import-compat-${i}`}>
                          <cm.Icon className="w-3 h-3" /> {cm.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
